import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useGroupPastedWorkbenchLines, useWorldOptions } from '../api/queries';
import type { GroupPastedWorkbenchResult, User } from '../api/types';
import { useToast } from '../components/Toast';

/**
 * "Workbenchbefehle nach Spieler gruppieren" (explizite Nutzeranfrage):
 * anders als der Export auf einer Kriegsplan-Detailseite kommt der Export
 * hier NICHT aus einer eigenen Planung, sondern wird als fertiger
 * DS-Workbench-"Ankunftsplaner"-Text eingefügt (z. B. von einem anderen
 * Stammesmitglied oder Tool) -- die Funktion löst nur die Quelldorf-ID
 * jeder Zeile gegen die zuletzt synchronisierten Weltdaten auf und teilt
 * die Zeilen danach in eine eigene Box je Absender-Spieler auf.
 */
export function AttackWorkbenchGroupingPage({ user }: { user: User }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const needsWorldChoice = user.world === null;
  const worldKey = searchParams.get('welt') ?? '';
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const [text, setText] = useState('');
  const [result, setResult] = useState<GroupPastedWorkbenchResult | null>(null);
  const group = useGroupPastedWorkbenchLines(activeWorld || undefined);
  const toast = useToast();

  const handleGroup = () => {
    if (!text.trim()) return;
    setResult(null);
    group.mutate(
      { text },
      {
        onSuccess: (data) => {
          setResult(data);
          if (data.unknownVillageLines.length) {
            toast.push('error', `${data.unknownVillageLines.length} Zeile(n) mit unbekannter Dorf-Spiel-ID übersprungen.`);
          }
          if (data.invalidLines.length) {
            toast.push('error', `${data.invalidLines.length} Zeile(n) im falschen Format übersprungen.`);
          }
        },
        onError: (err) => toast.push('error', err.message),
      },
    );
  };

  const copyLines = async (lines: string[]) => {
    if (!lines.length) return;
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.push('success', 'In die Zwischenablage kopiert.');
    } catch {
      toast.push('error', 'Zwischenablage nicht verfügbar -- bitte manuell markieren und kopieren.');
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Workbenchbefehle nach Spieler gruppieren</h1>
          <p className="muted">
            Einen fertigen DS-Workbench-"Ankunftsplaner"-Export einfügen -- die Zeilen werden anhand ihrer
            Quelldorf-Spiel-ID den zuletzt synchronisierten Weltdaten zugeordnet und nach Absender-Spieler aufgeteilt.
          </p>
        </div>
        <Link to={`/angriffsplaner${worldKey ? `?welt=${worldKey}` : ''}`} className="btn btn-sm">
          Zu den Kriegsplänen
        </Link>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setSearchParams({ welt: event.target.value }, { replace: true })} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && <p className="muted center pad">Wähle eine Welt, um Dörfer dieser Welt aufzulösen.</p>}

      {activeWorld && (
        <>
          <label className="field" style={{ marginTop: '12px' }}>
            <span>Workbench-Export</span>
            <textarea
              rows={8}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="quellDorfId&zielDorfId&einheit&ankunftMs&typ&false&false&einheiten"
            />
          </label>

          <div className="button-row">
            <button type="button" className="btn btn-primary" disabled={!text.trim() || group.isPending} onClick={handleGroup}>
              {group.isPending ? 'Wird gruppiert …' : 'Nach Spieler gruppieren'}
            </button>
          </div>

          {group.error instanceof ApiError && <div className="flash flash-error">{group.error.message}</div>}

          {result && (
            <div style={{ marginTop: '16px' }}>
              {result.byPlayer.length === 0 ? (
                <p className="muted center pad">Keine auflösbaren Zeilen.</p>
              ) : (
                result.byPlayer.map((playerGroup) => (
                  <div key={playerGroup.player ?? '__unbekannt__'} className="card card-callout" style={{ marginTop: '8px' }}>
                    <h4 style={{ margin: '0 0 8px' }}>
                      {playerGroup.player ?? 'Barbarendorf/unbekannt'} ({playerGroup.lines.length} Zeile
                      {playerGroup.lines.length === 1 ? '' : 'n'})
                    </h4>
                    <textarea rows={4} readOnly value={playerGroup.lines.join('\n')} />
                    <div className="button-row" style={{ marginTop: '8px' }}>
                      <button type="button" className="btn btn-sm" onClick={() => copyLines(playerGroup.lines)}>
                        Kopieren
                      </button>
                    </div>
                  </div>
                ))
              )}

              {result.unknownVillageLines.length > 0 && (
                <div className="card card-callout" style={{ marginTop: '8px' }}>
                  <h4 style={{ margin: '0 0 8px' }}>
                    Unbekannte Dorf-Spiel-ID ({result.unknownVillageLines.length} Zeile
                    {result.unknownVillageLines.length === 1 ? '' : 'n'})
                  </h4>
                  <p className="muted" style={{ margin: '0 0 8px' }}>
                    Diese Quelldörfer sind in den zuletzt synchronisierten Weltdaten nicht bekannt.
                  </p>
                  <textarea rows={3} readOnly value={result.unknownVillageLines.join('\n')} />
                </div>
              )}

              {result.invalidLines.length > 0 && (
                <div className="card card-callout" style={{ marginTop: '8px' }}>
                  <h4 style={{ margin: '0 0 8px' }}>
                    Falsches Format ({result.invalidLines.length} Zeile{result.invalidLines.length === 1 ? '' : 'n'})
                  </h4>
                  <textarea rows={3} readOnly value={result.invalidLines.join('\n')} />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
